import { useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";

// Your Google Analytics Measurement ID (G-XXXXXXXXXX)
// This is a public/publishable key and safe to include in code
const GA_MEASUREMENT_ID = "G-XXXXXXXXXX"; // Replace with your actual GA ID

const COOKIE_CONSENT_KEY = "cookie-consent";

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: unknown[];
  }
}

export function hasAnalyticsConsent(): boolean {
  return localStorage.getItem(COOKIE_CONSENT_KEY) === "accepted";
}

export function initGoogleAnalytics(): void {
  if (!hasAnalyticsConsent()) return;
  if (typeof window.gtag !== "undefined") return; // Already initialized

  // Create script elements
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Initialize dataLayer and gtag
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer.push(args);
  };
  window.gtag("js", new Date());
  window.gtag("config", GA_MEASUREMENT_ID, {
    anonymize_ip: true, // GDPR compliance
    cookie_flags: "SameSite=None;Secure",
  });
}

export function trackPageView(path: string): void {
  if (!hasAnalyticsConsent()) return;
  if (typeof window.gtag === "undefined") return;

  window.gtag("config", GA_MEASUREMENT_ID, {
    page_path: path,
  });
}

export function trackEvent(
  action: string,
  category: string,
  label?: string,
  value?: number
): void {
  if (!hasAnalyticsConsent()) return;
  if (typeof window.gtag === "undefined") return;

  window.gtag("event", action, {
    event_category: category,
    event_label: label,
    value: value,
  });
}

export function useGoogleAnalytics() {
  const location = useLocation();

  // Initialize GA on mount if consent exists
  useEffect(() => {
    initGoogleAnalytics();
  }, []);

  // Track page views on route change
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  const trackEventCallback = useCallback(
    (action: string, category: string, label?: string, value?: number) => {
      trackEvent(action, category, label, value);
    },
    []
  );

  return { trackEvent: trackEventCallback };
}
